import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { ProgramIndex } from '@vitesse/proto/vitesse/v1/program_index_pb.js';
import { customElement, property, state } from 'lit/decorators.js';
import './vitesse-code-view';
import type { VitesseCodeView } from './vitesse-code-view';
import type { CheckOptions } from '@vitesse/statechart-host';
// JetBrains Mono for the code document (IDE feel) — bundled (offline-friendly),
// loaded only with the program browser, not the whole app. Latin subset.
import '@fontsource/jetbrains-mono/latin-400.css';
import '@fontsource/jetbrains-mono/latin-500.css';
import '@fontsource/jetbrains-mono/latin-700.css';
import type { CodeLanguage } from './code-lang';
import { TreeSearch } from '@vitesse/base-ui/search';
import { parseSearchInput, matchesFilters, type SearchCommand } from '@vitesse/base-ui/search';
import { t } from '@vitesse/base-ui/i18n';

/*
 * <vitesse-code-browser> — the ONE reusable IDE-style code browser, app-wide.
 *
 * A real IDE shape: the SAME big-block chrome + search toolbar as the symbol grid
 * / logs views (`.vg-wrapper` + `.vg-toolbar`), and the whole program as ONE
 * continuous code document in the Monaco-backed code-view — which carries the real
 * VS Code minimap (opted in here). The code folds via Monaco; the toolbar's
 * collapse/expand-all folds the whole document; the search jumps to the first
 * matching block (`/type`, `/name`, composable).
 *
 * Domain-agnostic — the consumer supplies the body order (`blocks`) and an async
 * per-block `loadSource(key)`; the browser assembles them into the document and
 * owns search + fold-all. The PLC program viewer uses it now; the SCXML/model page
 * reuses the SAME element (its document = the chart XML).
 *
 * Light DOM so the shared `.vg-*` chrome + the code-view theme it identically.
 */

/** One body block in document order; carries the language + family. */
export interface CodeBlock {
  key: string;
  label: string;
  language: CodeLanguage;
  /** Block family (OB/FB/FC/DB/…) — drives the `/type` search command. */
  type?: string;
  /** Source size hint (unused by the assembled document; kept for callers). */
  estBytes?: number;
}

/** Search `/` commands for PLC programs — filter by block family or name. */
const ST_COMMANDS: SearchCommand<CodeBlock, void>[] = [
  {
    name: 'type',
    descKey: 'controller.cmdType',
    valueType: 'enum',
    enumValues: ['OB', 'FB', 'FC', 'DB', 'UDT', 'SFB', 'SFC'],
    placeholder: 'OB · FB · FC · DB',
    match: (block, value) => !value || (block.type ?? '').toUpperCase() === value.toUpperCase(),
    suggestValue: (block) => block.type,
  },
  {
    name: 'name',
    descKey: 'controller.cmdName',
    valueType: 'string',
    placeholder: 'block name',
    match: (block, value) => !value || block.label.toLowerCase().includes(value.toLowerCase()),
    suggestValue: (block) => block.label,
  },
];

function stBlockTextMatch(block: CodeBlock, query: string): boolean {
  return block.label.toLowerCase().includes(query.toLowerCase());
}

// ── SCXML search: real structural `/` commands over the chart, parsed from the
//    document — /state /event /target /data. Plain text matches any of those
//    values. Each entry carries the document line so a match scrolls Monaco to it.
interface ScxmlEntry {
  kind: 'state' | 'event' | 'target' | 'data';
  value: string;
  line: number; // 1-based line in the assembled document
}
function scxmlCmd(name: string, descKey: string, placeholder: string, kind: ScxmlEntry['kind']): SearchCommand<ScxmlEntry, void> {
  return {
    name,
    descKey,
    valueType: 'string',
    placeholder,
    match: (e, v) => e.kind === kind && (!v || e.value.toLowerCase().includes(v.toLowerCase())),
    suggestValue: (e) => (e.kind === kind ? e.value : undefined),
  };
}
const SCXML_COMMANDS: SearchCommand<ScxmlEntry, void>[] = [
  scxmlCmd('state', 'model.cmdState', 'state id', 'state'),
  scxmlCmd('event', 'model.cmdEvent', 'event name', 'event'),
  scxmlCmd('target', 'model.cmdTarget', 'target state', 'target'),
  scxmlCmd('data', 'model.cmdData', 'variable id', 'data'),
];
function scxmlEntryTextMatch(e: ScxmlEntry, query: string): boolean {
  return e.value.toLowerCase().includes(query.toLowerCase());
}
/** Parse the chart into searchable entries (state/event/target/data + line). */
function parseScxmlEntries(text: string): ScxmlEntry[] {
  const entries: ScxmlEntry[] = [];
  const push = (kind: ScxmlEntry['kind'], value: string, line: number) => entries.push({ kind, value, line });
  const lines = text.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const ln = i + 1;
    let m: RegExpExecArray | null;
    const stateRe = /<(?:state|parallel|final|history)\b[^>]*\bid\s*=\s*"([^"]+)"/g;
    while ((m = stateRe.exec(line))) push('state', m[1], ln);
    const initRe = /\binitial\s*=\s*"([^"]+)"/g;
    while ((m = initRe.exec(line))) push('target', m[1], ln);
    const evRe = /\bevent\s*=\s*"([^"]+)"/g;
    while ((m = evRe.exec(line))) for (const ev of m[1].split(/\s+/).filter(Boolean)) push('event', ev, ln);
    const tgRe = /\btarget\s*=\s*"([^"]+)"/g;
    while ((m = tgRe.exec(line))) push('target', m[1], ln);
    const dataRe = /<data\b[^>]*\bid\s*=\s*"([^"]+)"/g;
    while ((m = dataRe.exec(line))) push('data', m[1], ln);
  }
  return entries;
}

const PROGRAM_FONT = "'JetBrains Mono', 'SF Mono', ui-monospace, monospace";
const FETCH_CONCURRENCY = 8; // parallel block-source fetches while assembling the document
const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');

// Inline glyphs — match the symbol grid / logs toolbar verbatim.
const SEARCH_ICON = html`<svg
  class="vg-search-icon"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>`;
const CLEAR_ICON = html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m15 9-6 6"></path><path d="m9 9 6 6"></path></svg>`;
// chevrons-down-up = collapse all; chevrons-up-down = expand all (matches .vg-btn-expand)
const COLLAPSE_ALL_ICON = html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m7 20 5-5 5 5"></path><path d="m7 4 5 5 5-5"></path></svg>`;
const EXPAND_ALL_ICON = html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m7 15 5 5 5-5"></path><path d="m7 9 5-5 5 5"></path></svg>`;

@customElement('vitesse-code-browser')
export class VitesseCodeBrowser extends LitElement {
  protected createRenderRoot(): HTMLElement {
    return this; // light DOM → shared .vg-* chrome + code-view theme
  }

  /** The body, in document order (the flattened blocks). */
  @property({ attribute: false }) blocks: CodeBlock[] = [];
  /** Async per-block source loader. */
  @property({ attribute: false }) loadSource?: (key: string) => Promise<string>;
  @property() searchPlaceholder = '';
  @property() emptyHint = '';
  /** Search mode: 'st' = PLC block commands (/type /name) that jump to a block;
   *  'scxml' = chart commands (/state /event /target /data) that scroll to a line. */
  @property() searchMode: 'st' | 'scxml' = 'st';
  /** A model's interface and children, handed to the code view so it checks the statechart and answers hovers. */
  @property({ attribute: false }) scxmlCheck?: CheckOptions;

  @state() private query = '';
  @state() private programText = '';
  @state() private loading = false;
  @state() private error = false;
  @state() private allFolded = false;

  private blockStartLine = new Map<string, number>(); // key → 1-based line in the document
  private scxmlEntries: ScxmlEntry[] = []; // searchable chart entries (scxml mode)
  private assembleToken = 0;
  private treeSearch?: TreeSearch<any, void>; // CodeBlock (st) | ScxmlEntry (scxml)
  private mainRO?: ResizeObserver;

  // ⌘/ focuses the search (capture phase so it beats Monaco's own ⌘/ toggle-comment).
  private readonly onKeydown = (e: KeyboardEvent): void => {
    if ((e.metaKey || e.ctrlKey) && e.key === '/') {
      const input = this.querySelector<HTMLInputElement>('.vg-search-input');
      if (input) {
        e.preventDefault();
        e.stopPropagation();
        input.focus();
      }
    }
  };

  // ── lifecycle ──────────────────────────────────────────────────────────────
  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKeydown, true);
  }
  protected firstUpdated(): void {
    this.setupSearch();
    // Bound the host to the viewport so Monaco fills it + inner-scrolls (it needs a
    // definite height) rather than the page growing to the full document height.
    const main = this.closest<HTMLElement>('.main-content');
    if (main) {
      this.mainRO = new ResizeObserver(() => this.fitHeight());
      this.mainRO.observe(main);
    }
    requestAnimationFrame(() => this.fitHeight());
  }
  protected updated(changed: PropertyValues): void {
    if (changed.has('blocks')) void this.assemble();
    if (changed.has('query')) this.scrollToFirstMatch();
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onKeydown, true);
    this.treeSearch?.destroy();
    this.treeSearch = undefined;
    this.mainRO?.disconnect();
    this.mainRO = undefined;
  }

  /** Size the host to fill from its top to the bottom of the scroll viewport, so
   *  the code-view inner-scrolls instead of the page growing to the document. */
  private readonly fitHeight = (): void => {
    const main = this.closest<HTMLElement>('.main-content');
    if (!main) return;
    const top = this.getBoundingClientRect().top - main.getBoundingClientRect().top;
    const h = main.clientHeight - top - 8; // small bottom gap
    if (h > 200) this.style.height = `${Math.floor(h)}px`;
  };

  private get codeView(): VitesseCodeView | null {
    return this.querySelector<VitesseCodeView>('vitesse-code-view');
  }

  // ── assemble the whole program into one continuous document ──────────────────
  private async assemble(): Promise<void> {
    const token = ++this.assembleToken;
    this.allFolded = false;
    if (!this.loadSource || this.blocks.length === 0) {
      this.programText = '';
      this.blockStartLine = new Map();
      this.scxmlEntries = [];
      this.loading = false;
      this.error = false;
      return;
    }
    this.loading = true;
    this.error = false;
    try {
      // Fetch every block's source (bounded concurrency); a failed block becomes
      // a comment placeholder so one bad block can't blank the whole document.
      const texts = await mapLimit(this.blocks, FETCH_CONCURRENCY, (b) =>
        this.loadSource!(b.key).catch(() => `(* ${b.label}: source unavailable *)`),
      );
      if (token !== this.assembleToken) return; // superseded by a newer program
      const starts = new Map<string, number>();
      const parts: string[] = [];
      let line = 1;
      for (let i = 0; i < this.blocks.length; i++) {
        starts.set(this.blocks[i].key, line);
        const text = texts[i] ?? '';
        parts.push(text);
        line += countLines(text) + 1; // +1 = the blank separator line between blocks
      }
      this.blockStartLine = starts;
      this.programText = parts.join('\n\n');
      this.scxmlEntries = this.searchMode === 'scxml' ? parseScxmlEntries(this.programText) : [];
      this.loading = false;
    } catch {
      if (token === this.assembleToken) {
        this.error = true;
        this.loading = false;
      }
    }
  }

  // ── search (shared TreeSearch — same bar + highlight + ⌘/ as every view) ─────
  private setupSearch(): void {
    const wrap = this.querySelector<HTMLElement>('.vg-search-wrap');
    if (!wrap) return;
    const scxml = this.searchMode === 'scxml';
    this.treeSearch = new TreeSearch<any, void>(wrap, {
      commands: scxml ? SCXML_COMMANDS : ST_COMMANDS,
      ctx: () => undefined,
      iterate: () => (scxml ? this.scxmlEntries : this.blocks),
      valueForCommand: (cmd, item) => cmd.suggestValue?.(item),
      textMatch: scxml ? scxmlEntryTextMatch : stBlockTextMatch,
      onChange: (q) => {
        this.query = q;
      },
    });
  }
  private clearSearch(): void {
    this.treeSearch?.setValue('');
    this.query = '';
  }

  // ── interactions ─────────────────────────────────────────────────────────────
  /** Search → jump to the first matching block (no tree list to filter). */
  private scrollToFirstMatch(): void {
    const q = this.query.trim();
    if (!q) return;
    // SCXML: match a chart entry (/state /event /target /data or plain text) and
    // scroll Monaco to its line.
    if (this.searchMode === 'scxml') {
      const parsed = parseSearchInput<ScxmlEntry, void>(q, SCXML_COMMANDS);
      for (const e of this.scxmlEntries) {
        if (matchesFilters<ScxmlEntry, void>(e, parsed, undefined, scxmlEntryTextMatch)) {
          this.codeView?.scrollToLine(e.line);
          return;
        }
      }
      return;
    }
    // Multi-block program: jump to the first matching block.
    const parsed = parseSearchInput<CodeBlock, void>(q, ST_COMMANDS);
    for (const b of this.blocks) {
      if (matchesFilters<CodeBlock, void>(b, parsed, undefined, stBlockTextMatch)) {
        const line = this.blockStartLine.get(b.key);
        if (line) this.codeView?.scrollToLine(line);
        return;
      }
    }
  }
  private toggleFoldAll(): void {
    const cv = this.codeView;
    if (!cv) return;
    if (this.allFolded) cv.unfoldAll();
    else cv.foldAll();
    this.allFolded = !this.allFolded;
  }

  // ── render ───────────────────────────────────────────────────────────────────
  render() {
    // The whole program surface is one purchase: browsing, searching and reading a
    // block are the same capability, so the gate wraps the view rather than each
    // control inside it.
    return html`<vitesse-gated .rpc=${ProgramIndex.method.getProgram}>
      <div class="vg-wrapper cb-wrapper">
      <div class="vg-toolbar">
        <div class="vg-search-wrap">
          ${SEARCH_ICON}
          <div class="vg-search-highlight"></div>
          <input
            class="vg-search-input"
            type="text"
            placeholder=${this.searchPlaceholder}
            spellcheck="false"
            autocomplete="off"
          />
          ${this.query
            ? html`<button class="vg-search-clear" title="Clear" @click=${this.clearSearch}>${CLEAR_ICON}</button>`
            : nothing}
          <kbd class="vg-search-shortcut"><span>${isMac ? '⌘' : 'Ctrl'}</span>/</kbd>
        </div>
        <div class="vg-toolbar-actions">
          <button
            class="vg-toolbar-btn vg-btn-expand"
            title=${this.allFolded ? t('grid.expandAll') : t('grid.collapseAll')}
            @click=${this.toggleFoldAll}
          >
            ${this.allFolded ? EXPAND_ALL_ICON : COLLAPSE_ALL_ICON}
          </button>
        </div>
      </div>
      <div class="cb-body">
        <div class="cb-code">${this.codeArea()}</div>
      </div>
    </div>
    </vitesse-gated>`;
  }

  private codeArea() {
    if (this.error) return html`<div class="cb-state cb-error">${t('controller.programSourceError')}</div>`;
    if (this.loading && !this.programText) return html`<div class="cb-state">${t('controller.programLoading')}</div>`;
    if (!this.programText) return html`<div class="cb-state">${this.emptyHint || nothing}</div>`;
    return html`<vitesse-code-view
      .value=${this.programText}
      language=${this.blocks[0]?.language ?? 'st'}
      .fontFamily=${PROGRAM_FONT}
      ?minimap=${true}
      .scxmlCheck=${this.searchMode === 'scxml' ? this.scxmlCheck : undefined}
    ></vitesse-code-view>`;
  }
}

// ── helpers ────────────────────────────────────────────────────────────────────
/** Map with bounded concurrency (avoid a fetch storm on big programs). */
async function mapLimit<T, R>(items: T[], limit: number, fn: (item: T, i: number) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}
function countLines(text: string): number {
  if (!text) return 1;
  let n = 1;
  for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) === 10) n++;
  return n;
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-code-browser': VitesseCodeBrowser;
  }
}
